class WaveAnimation {
    constructor(canvasId) {
        this.canvas = document.getElementById(canvasId);
        this.ctx = this.canvas.getContext('2d');
        this.waveWidth = 370; // wave width from peak to peak
        this.waveHeight = 70; // max height of the wave
        this.speedVariation = 0.001; //Speed Random Variation
        this.speed = 0.02; // speed of the wave animation
        this.points = []; // points that define the wave
        this.setCanvasSize();
        // Bind the animate method once in the constructor
        this.animate = this.animate.bind(this);

        // Debounce the resize event listener
        window.addEventListener('resize', this.debounce(() => this.setCanvasSize(), 250));
        this.initPoints();
        this.animate();

        // Create an Intersection Observer to monitor visibility
        this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // Element is in view, start animation if not already running
                    if (!this.animationFrameId) {
                        this.animationFrameId = requestAnimationFrame(this.animate);
                    }
                } else {
                    // Element is out of view, stop animation
                    if (this.animationFrameId) {
                        cancelAnimationFrame(this.animationFrameId);
                        this.animationFrameId = null;
                    }
                }
            });
        }, { threshold: 0.1 }); // Adjust threshold as needed

        // Start observing the canvas
        this.observer.observe(this.canvas);
    }

    setCanvasSize() {
        this.canvas.width = this.canvas.offsetWidth;
        this.canvas.height = this.waveHeight; // fixed height
        this.initPoints(); // reinitialize points when canvas size changes
    }
    debounce(func, wait) {
        let timeout;
        return function() {
            const context = this, args = arguments;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    }
    initPoints() {
        // Clear any previous points
        this.points = [];

        // Calculate the number of points based on the canvas width and wave width
        let numberOfPoints = Math.ceil(this.canvas.width / (this.waveWidth / 4)) + 1;

        // Decide the initial sign for speed (-1 or 1)
        let speedSign = Math.random() < 0.5 ? -1 : 1;

        // Initialize wave points with random amplitudes and alternating speeds
        for (let i = 0; i < numberOfPoints; i++) {
            let x = i * (this.waveWidth / 4);
            this.points.push({
                x: x,
                amplitude: this.waveHeight / 2 * Math.random(),
                phase: Math.random() * Math.PI * 2,
                // speed is the base speed plus a random variation within the speedVariation range
                speed: (this.speed + speedSign * Math.random() * this.speedVariation)
            });
            // Alternate the sign for the next point
            speedSign *= -1;
        }
    }

    updatePoints() {
        // Randomly adjust the phase and amplitude of each point for the wavy effect
        this.points.forEach(point => {
            point.phase += point.speed;
            // Calculate y to oscillate around the canvas midpoint, with amplitude determining the range
            point.y = this.waveHeight / 2 + point.amplitude * Math.sin(point.phase);
        });
    }

    draw() {
        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        ctx.beginPath();
        ctx.moveTo(this.points[0].x, this.canvas.height - this.points[0].y);

        // Draw curves through the points
        for (let i = 0; i < this.points.length - 1; i++) {
            const start = this.points[i];
            const end = this.points[i + 1];
            const cpX = (start.x + end.x) / 2;
            const startY = this.canvas.height - start.y;
            const endY = this.canvas.height - end.y;

            ctx.quadraticCurveTo(start.x, startY, cpX, (startY + endY) / 2);
        }

        // Draw the final curve to the last point
        const lastPoint = this.points[this.points.length - 1];
        ctx.quadraticCurveTo(lastPoint.x, this.canvas.height - lastPoint.y, lastPoint.x, this.canvas.height - lastPoint.y);

        // Close off the Shape
        ctx.lineTo(this.canvas.width, this.canvas.height);
        ctx.lineTo(0, this.canvas.height);
        ctx.closePath();

        // Fill the wave with white color
        ctx.fillStyle = '#FAFAFA';
        ctx.fill();
    }

    animate() {
        this.updatePoints();
        this.draw();
        this.animationFrameId = requestAnimationFrame(this.animate);
    }
}

export default WaveAnimation;
